import { useCallback, useMemo } from "react";

import { CheckIcon } from "@phosphor-icons/react";
import pluralize from "pluralize";
import { match } from "ts-pattern";

import Accordion from "@gx/dls/accordion/Accordion";
import Button, { ButtonSize } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import { InputLabel } from "@gx/dls/inputs/Input";
import NumberInput from "@gx/dls/inputs/NumberInput";
import SliderInput from "@gx/dls/inputs/SliderInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Text, { TextSize } from "@gx/dls/text/Text";
import Widget from "@gx/dls/widget/Widget";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import CreateSourceModalHeader from "@/pages/sources/components/create/components/CreateSourceModalHeader";
import CreateSourceModalWrapper from "@/pages/sources/components/create/components/CreateSourceModalWrapper";
import CreateSourceNameInput from "@/pages/sources/components/create/components/CreateSourceNameInput";
import { CREATE_SOURCE_WIDGET_INPUT_WIDTH } from "@/pages/sources/components/create/constants";
import { CreateSourceWikipediaActionType } from "@/pages/sources/components/create/sources/wikipedia/actions";
import {
  SOURCE_WIKIPEDIA_DEFAULT_MAX_PAGES,
  SOURCE_WIKIPEDIA_MAX_CRAWL_DEPTH,
  SOURCE_WIKIPEDIA_MAX_LINKS_PER_PAGE,
  SOURCE_WIKIPEDIA_MAX_MAX_PAGES,
  SOURCE_WIKIPEDIA_MIN_CRAWL_DEPTH,
  SOURCE_WIKIPEDIA_MIN_LINKS_PER_PAGE,
  SOURCE_WIKIPEDIA_MIN_MAX_PAGES,
  SOURCE_WIKIPEDIA_PAGE_ESTIMATE_SEVERITY_TO_TEXT_VARIANT_MAP,
} from "@/pages/sources/components/create/sources/wikipedia/constants";
import CreateSourceWikipediaProvider from "@/pages/sources/components/create/sources/wikipedia/CreateSourceWikipediaProvider";
import { useCreateSourceWikipedia } from "@/pages/sources/components/create/sources/wikipedia/hooks";
import {
  CreateSourceWikipediaPhase,
  WikipediaPageEstimateSeverity,
} from "@/pages/sources/components/create/sources/wikipedia/types";
import {
  calculateEstimatedPages,
  getPageEstimateSeverity,
} from "@/pages/sources/components/create/sources/wikipedia/utils";
import { CreateSourceStep } from "@/pages/sources/components/create/types";

import { NOOP } from "@/utils/helpers";

interface CreateSourceWikipediaProps {
  onClose: () => void;
  onBack: () => void;
}

const CreateSourceWikipediaContent = ({ onClose, onBack }: CreateSourceWikipediaProps) => {
  const { state, dispatch, save, isDisabled, isValid, nameError, seedUrlError } =
    useCreateSourceWikipedia({
      onSuccess: () => onClose(),
    });

  const handleSourceNameChange = useCallback(
    (name: string) => {
      dispatch({ type: CreateSourceWikipediaActionType.SET_SOURCE_NAME, payload: name });
    },
    [dispatch],
  );

  const handleSeedUrlChange = useCallback(
    (url: string) => {
      dispatch({ type: CreateSourceWikipediaActionType.SET_SEED_URL, payload: url });
    },
    [dispatch],
  );

  const handleLinksPerPageChange = useCallback(
    (value: number) => {
      dispatch({ type: CreateSourceWikipediaActionType.SET_LINKS_PER_PAGE, payload: value });
    },
    [dispatch],
  );

  const handleCrawlDepthChange = useCallback(
    (value: number) => {
      dispatch({ type: CreateSourceWikipediaActionType.SET_CRAWL_DEPTH, payload: value });
    },
    [dispatch],
  );

  const handleMaxPagesChange = useCallback(
    (value: number | null) => {
      if (value === null) {
        dispatch({
          type: CreateSourceWikipediaActionType.SET_MAX_PAGES,
          payload: SOURCE_WIKIPEDIA_DEFAULT_MAX_PAGES,
        });
        return;
      }
      const clampedValue = Math.max(
        SOURCE_WIKIPEDIA_MIN_MAX_PAGES,
        Math.min(SOURCE_WIKIPEDIA_MAX_MAX_PAGES, value),
      );
      dispatch({
        type: CreateSourceWikipediaActionType.SET_MAX_PAGES,
        payload: clampedValue,
      });
    },
    [dispatch],
  );

  const estimatedPages = useMemo(
    () => calculateEstimatedPages(state.linksPerPage, state.crawlDepth),
    [state.linksPerPage, state.crawlDepth],
  );

  const severity = useMemo(
    () => getPageEstimateSeverity(estimatedPages, state.maxPages),
    [estimatedPages, state.maxPages],
  );

  const estimateText = useMemo(() => {
    if (severity === WikipediaPageEstimateSeverity.ERROR) {
      return `${state.maxPages} pages max`;
    }
    return `~${estimatedPages} ${pluralize("page", estimatedPages)}`;
  }, [severity, estimatedPages, state.maxPages]);

  const renderButton = useMemo(() => {
    return match(state.phase)
      .with(CreateSourceWikipediaPhase.IN_PROGRESS, () => (
        <Button
          label="Creating..."
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={true}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .with(CreateSourceWikipediaPhase.COMPLETED, () => (
        <Button
          label="Done"
          icon={CheckIcon}
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={false}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .otherwise(() => (
        <Button
          label="Save"
          size={ButtonSize.LARGE}
          onClick={() => void save()}
          isLoading={false}
          isDisabled={!isValid}
          fillWidth
        />
      ));
  }, [state.phase, save, isValid]);

  return (
    <CreateSourceModalWrapper step={CreateSourceStep.CONFIGURE}>
      <CreateSourceModalHeader
        sourceType={SourceType.WIKIPEDIA}
        onClose={onClose}
        onBack={onBack}
      />
      <FlexItem>
        <HorizontalDivider />
      </FlexItem>

      <FlexItem grow={1} minHeight={0} fillWidth overflow="auto" padding="16px">
        <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
          <CreateSourceNameInput
            value={state.sourceName}
            onChange={handleSourceNameChange}
            error={nameError}
            isDisabled={isDisabled}
          />

          <TextInput
            label="Wikipedia URL"
            labelTooltip="The Wikipedia page to start from. Related pages will be discovered based on the settings below."
            value={state.seedUrl}
            onChange={handleSeedUrlChange}
            placeholder="https://en.wikipedia.org/wiki/..."
            error={seedUrlError ?? undefined}
            isRequired
            isDisabled={isDisabled}
            fillWidth
          />

          <Accordion
            header="Advanced configuration"
            metric={
              <Text
                variant={SOURCE_WIKIPEDIA_PAGE_ESTIMATE_SEVERITY_TO_TEXT_VARIANT_MAP[severity]}
                size={TextSize.BODY_SM}
              >
                {estimateText}
              </Text>
            }
          >
            <SliderInput
              label="Links per page"
              labelTooltip="How many related article links to follow from each page."
              value={state.linksPerPage}
              onChange={handleLinksPerPageChange}
              min={SOURCE_WIKIPEDIA_MIN_LINKS_PER_PAGE}
              max={SOURCE_WIKIPEDIA_MAX_LINKS_PER_PAGE}
              step={1}
              isDisabled={isDisabled}
              fillWidth
            />

            <SliderInput
              label="Crawl depth"
              labelTooltip="How many levels of links to follow."
              value={state.crawlDepth}
              onChange={handleCrawlDepthChange}
              min={SOURCE_WIKIPEDIA_MIN_CRAWL_DEPTH}
              max={SOURCE_WIKIPEDIA_MAX_CRAWL_DEPTH}
              step={1}
              isDisabled={isDisabled}
              fillWidth
            />

            <Widget fillWidth noHover>
              <FlexWrapper
                alignItems={AlignItems.CENTER}
                justifyContent={JustifyContent.SPACE_BETWEEN}
                fillWidth
              >
                <InputLabel
                  label="Page limit"
                  labelTooltip="Crawl stops when this limit is reached. Maximum is 100 pages."
                />
                <NumberInput
                  value={state.maxPages}
                  onChange={handleMaxPagesChange}
                  placeholder={String(SOURCE_WIKIPEDIA_DEFAULT_MAX_PAGES)}
                  min={SOURCE_WIKIPEDIA_MIN_MAX_PAGES}
                  max={SOURCE_WIKIPEDIA_MAX_MAX_PAGES}
                  isDisabled={isDisabled}
                  width={CREATE_SOURCE_WIDGET_INPUT_WIDTH}
                />
              </FlexWrapper>
            </Widget>
          </Accordion>
        </FlexWrapper>
      </FlexItem>

      <FlexWrapper direction={FlexDirection.COLUMN} fillWidth>
        <HorizontalDivider />
        <FlexWrapper padding="16px" fillWidth>
          {renderButton}
        </FlexWrapper>
      </FlexWrapper>
    </CreateSourceModalWrapper>
  );
};

const CreateSourceWikipedia = (props: CreateSourceWikipediaProps) => (
  <CreateSourceWikipediaProvider>
    <CreateSourceWikipediaContent {...props} />
  </CreateSourceWikipediaProvider>
);

export default CreateSourceWikipedia;
